import { Film, Grid3X3, CircleDot } from "lucide-react";
import { Card } from "@/components/ui/Card";
import { formatNumber, formatPercent } from "@/utils/format";
import type {
  MarketingContentGoals,
  MarketingProductionView,
  ProductionGalleryKind,
} from "@/services/marketing";
import { cn } from "@/utils/cn";

interface Props {
  production: MarketingProductionView | null | undefined;
  goals: MarketingContentGoals;
  loading?: boolean;
  onOpenGallery: (kind: ProductionGalleryKind) => void;
}

const ITEMS: {
  key: ProductionGalleryKind;
  label: string;
  icon: typeof Grid3X3;
  border: string;
  iconBg: string;
}[] = [
  {
    key: "feed",
    label: "Feed",
    icon: Grid3X3,
    border: "border-l-indigo-500",
    iconBg: "text-indigo-600",
  },
  {
    key: "reels",
    label: "Reels",
    icon: Film,
    border: "border-l-pink-500",
    iconBg: "text-pink-600",
  },
  {
    key: "stories",
    label: "Stories",
    icon: CircleDot,
    border: "border-l-amber-500",
    iconBg: "text-amber-600",
  },
];

function goalProgress(count: number, goal: number): number | null {
  if (goal <= 0) return null;
  return (count / goal) * 100;
}

export function MarketingProductionSection({
  production,
  goals,
  loading,
  onOpenGallery,
}: Props) {
  return (
    <section>
      <div className="mb-3">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
          Produção da equipe
        </p>
        <p className="text-xs text-slate-400">
          Clique no card para ver capas · meta calculada por dia/semana/mês
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {ITEMS.map(({ key, label, icon: Icon, border, iconBg }) => {
          const count = production?.[key] ?? 0;
          const goal = goals[key] ?? 0;
          const progress = goalProgress(count, goal);
          const metGoal = progress !== null && progress >= 100;
          const remaining = Math.max(0, goal - count);
          const subtitle =
            goal > 0
              ? `Meta: ${formatNumber(count)}/${formatNumber(goal)} - ${formatPercent(progress ?? 0, 0)}${
                  count > goal
                    ? " · Acima da meta"
                    : count >= goal
                      ? " · Meta atingida"
                      : ` · Faltam ${formatNumber(remaining)}`
                }`
              : "Meta não definida";

          return (
            <div key={key} className="flex flex-col gap-2">
              <Card
                title={label}
                value={loading ? undefined : formatNumber(count)}
                subtitle={loading ? undefined : subtitle}
                icon={<Icon className={cn("h-5 w-5", iconBg)} />}
                loading={loading}
                className={cn("border-l-4 cursor-pointer hover:shadow-md hover:-translate-y-0.5", border)}
                infoHint={
                  key === "stories"
                    ? "Stories são arquivados a cada 2h (um registro por ID). Clique para ver o histórico capturado."
                    : "Clique para ver as capas publicadas no período."
                }
                onClick={() => onOpenGallery(key)}
              />
              {!loading && goal > 0 && (
                <div className="h-1.5 overflow-hidden rounded-full bg-slate-100 px-1">
                  <div
                    className={cn(
                      "h-full rounded-full transition-all",
                      metGoal ? "bg-emerald-500" : "bg-brand-500",
                    )}
                    style={{ width: `${Math.min(progress ?? 0, 100)}%` }}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
